// Shared vocabulary of HR › Payroll (UI-3A). Period statuses and transitions are
// the server's (payroll.runtime.service PERIOD_TRANSITIONS); this file only says
// what each means for the HR operator and which action comes next.

export const HR_PAYROLL_BASE = "/business/hr/payroll";
export const WAITING_PATH = `${HR_PAYROLL_BASE}/waiting`;
export const HISTORY_PATH = `${HR_PAYROLL_BASE}/history`;
export const periodPath = (id) => `${HR_PAYROLL_BASE}/periods/${id}`;
export const SCHEDULES_PATH = `${HR_PAYROLL_BASE}/schedules`;
export const schedulePath = (id, section = "") => `${SCHEDULES_PATH}/${id}${section ? `/${section}` : ""}`;
// Work and Waiting hold the payroll still in motion, a short list; the server's page cap is 100. History is paged.
export const PERIOD_LIMIT = 100;
export const HISTORY_PAGE_SIZE = 25;

export const periodStatus = (row) => String(row?.status || "").toLowerCase();
export const wasReturned = (row) => row?.last_finance_review?.action === "returned";

const toDate = (value) => {
  if (!value) return null;
  const date = new Date(String(value).length <= 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export function shortDate(value) {
  const date = toDate(value);
  return date ? date.toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" }) : "";
}

/** "Oct 1 – 15, 2026", "Sep 28 – Oct 4, 2026", "Dec 28, 2026 – Jan 3, 2027" */
export function dateRange(row) {
  const start = toDate(row?.start_date);
  const end = toDate(row?.end_date);
  if (!start || !end) return "";
  const month = (d) => d.toLocaleDateString("en-PH", { month: "short" });
  if (start.getFullYear() !== end.getFullYear()) {
    return `${month(start)} ${start.getDate()}, ${start.getFullYear()} – ${month(end)} ${end.getDate()}, ${end.getFullYear()}`;
  }
  const endPart = start.getMonth() === end.getMonth() ? `${end.getDate()}` : `${month(end)} ${end.getDate()}`;
  return `${month(start)} ${start.getDate()} – ${endPart}, ${end.getFullYear()}`;
}

export const periodName = (row) => row?.name || dateRange(row);

/** "Semi-monthly · Oct 1 – 15, 2026 · pay date Oct 20, 2026" */
export function periodFacts(row) {
  const schedule = row?.payroll_schedule_name
    || (row?.payroll_schedule_frequency ? String(row.payroll_schedule_frequency).replace(/_/g, "-") : "");
  return [
    schedule ? schedule.charAt(0).toUpperCase() + schedule.slice(1) : null,
    dateRange(row),
    row?.pay_date ? `pay date ${shortDate(row.pay_date)}` : null,
  ].filter(Boolean).join(" · ");
}

/** A draft whose dates have not begun is upcoming, not today's work. */
export function isUpcoming(row, today = new Date()) {
  const start = toDate(row?.start_date);
  if (!start || periodStatus(row) !== "draft") return false;
  const midnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return start > midnight;
}

/**
 * Which lane a period belongs to.
 *   work    — HR acts next: draft, preparing, calculated, returned, approved
 *   waiting — someone else acts: Finance reviewing, Finance paying salaries
 *   history — closed: settled, cancelled
 */
export function periodLane(row) {
  const status = periodStatus(row);
  if (["draft", "collecting_inputs", "computed", "approved"].includes(status)) return "work";
  if (["under_review", "released"].includes(status)) return "waiting";
  return "history";
}

/**
 * Work groups, in the order HR should handle them: the release Finance is
 * waiting on first, then corrections Finance asked for, then preparation.
 */
export const WORK_GROUPS = [
  { key: "release", title: "Ready to release", match: (row) => periodStatus(row) === "approved" },
  { key: "returned", title: "Returned by Finance", match: (row) => ["collecting_inputs", "computed"].includes(periodStatus(row)) && wasReturned(row) },
  { key: "calculated", title: "Calculated", match: (row) => periodStatus(row) === "computed" && !wasReturned(row) },
  { key: "preparing", title: "Preparing", match: (row) => periodStatus(row) === "collecting_inputs" && !wasReturned(row) },
  { key: "draft", title: "Not started", match: (row) => periodStatus(row) === "draft" && !isUpcoming(row) },
  { key: "upcoming", title: "Upcoming", match: (row) => isUpcoming(row) },
];

/** The state in words, and its tone (DESIGN.md › How status is shown). */
export function stateOf(row) {
  const status = periodStatus(row);
  if (status === "approved") return { label: "Approved by Finance — ready to release payslips", tone: "success" };
  if (wasReturned(row) && ["collecting_inputs", "computed"].includes(status)) return { label: "Returned by Finance", tone: "warning" };
  switch (status) {
    case "draft": return { label: "Not started", tone: "neutral" };
    case "collecting_inputs": return { label: "Preparing — not calculated yet", tone: "neutral" };
    case "computed": return { label: "Calculated", tone: "neutral" };
    case "under_review": return { label: "With Finance for review", tone: "neutral" };
    case "released": return { label: "Released — Finance is paying salaries", tone: "neutral" };
    case "settled": return { label: "Settled", tone: "success" };
    case "cancelled": return { label: "Cancelled", tone: "neutral" };
    default: return { label: "Unknown state", tone: "neutral" };
  }
}

/**
 * The one next step HR takes on a period, by its status. Labels name the
 * action; whether the server allows it right now is the server's decision.
 */
export function nextStep(row) {
  const status = periodStatus(row);
  if (status === "draft") return { kind: "open", label: "Start preparing" };
  if (status === "collecting_inputs") return { kind: "calculate", label: "Calculate" };
  if (status === "computed") return { kind: "review", label: "Review and send" };
  if (status === "approved") return { kind: "release", label: "Release payslips" };
  return null;
}

/** Plain server message for a refused action; never a status code. */
export function actionError(error, fallback) {
  const message = error?.serverMessage || error?.response?.data?.message || "";
  return String(message).replace(/^Request failed( with status code)?:?\s*\d{3}\s*[—-]?\s*/i, "").trim() || fallback;
}
